/**
 * EventsBlock.tsx — sección "Próximos eventos" del Home.
 *
 * Extraído de `pages/Home.tsx` (2026-09-22). Desktop: grid de 3 columnas.
 * Mobile/tablet: carrusel horizontal con snap e indicadores.
 *
 * Cada card es `EventDarkCard`: la obra del evento arriba y un panel violeta
 * sólido abajo con título, modalidad, horario, precio y CTA.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

import { Container } from '../../components/layout/Container';
import { Image } from '../../components/ui/Image';
import { Skeleton } from '../../components/ui/Skeleton';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';
import { CalendarioIcon } from '../../components/icons';
import { cn } from '../../lib/cn';
import { Routes } from '../../lib/routes';
import { usePageCopy } from '../site-content/usePageCopy';
import { useUpcomingEvents, type UpcomingEvent } from './api';
import { copFormatter, eventKindCopy, eventModalityCopy, formatEventMeta } from './format';
import { SectionHeading } from './SectionHeading';
import {
  EXPOSICION_LABEL,
  formatRangeShort,
  isExposicion,
  isMultiDay,
} from '../events/event-display';
import { StarDot } from './ornaments';

function EventDarkCard({ event }: { event: UpcomingEvent }) {
  const meta = formatEventMeta(event.starts_at);
  const price = event.price_cop ? copFormatter.format(event.price_cop) : null;
  // Exposición (CEC-261009-8e6d): sin hora, cupos ni precio.
  const exposicion = isExposicion({
    slug: event.slug,
    category_slug: event.event_categories?.slug,
  });
  const fecha = isMultiDay(event.starts_at, event.ends_at)
    ? formatRangeShort(event.starts_at, event.ends_at)
    : `${meta.day} ${meta.month}`;
  const tipo = exposicion ? EXPOSICION_LABEL : eventKindCopy[event.kind];

  return (
    <Link
      to={Routes.eventDetail(event.slug)}
      aria-label={`Ver ${event.title}`}
      className={cn(
        // Card editorial clásica: padre con `overflow-hidden rounded-lg` para
        // que la foto arriba y el bloque violeta abajo HEREDEN las esquinas
        // (arriba foto, abajo violeta). Mismo esqueleto que `ProductMiniCard`
        // (cream-50 abajo) para mantener armonía de la grid del Home.
        'group flex h-full flex-col overflow-hidden rounded-lg',
        'border border-gold-500/30',
        'shadow-elev2',
        'transition-all duration-2 ease-out hover:-translate-y-0.5',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50',
      )}
    >
      {/* ── Mitad superior: LA OBRA, completa ────────────────────────────
          QA 2026-09-22: antes esto era `object-cover` sobre una caja 4/3 con
          la fecha y el chip de cupos flotando encima. Con obras cuadradas
          —como el políptico de la Bienal, 1254×1254— el cover recortaba ~25 %
          arriba y abajo, partía los lienzos por la mitad y los chips quedaban
          impresos sobre la pieza. En una plataforma de arte eso no es un
          detalle: mutila la obra.

          Ahora la caja conserva su proporción (el grid necesita un ritmo
          uniforme) pero la imagen va `object-contain` sobre el violeta de
          marca, con un respiro de padding. La pieza se lee entera, cualquiera
          sea su formato, y el marco violeta se percibe como paspartú. Los
          chips bajaron al panel de texto. */}
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-vivid">
        {event.hero_url && (
          <Image
            src={event.hero_url}
            alt=""
            width={480}
            height={360}
            ratio="auto"
            fit="contain"
            // `bg-transparent` pisa el `bg-cream-200` que `Image` usa como
            // placeholder: con `contain` ese fondo sería visible como franjas
            // crema a los lados. Aquí queremos ver el violeta del paspartú.
            className="h-full w-full bg-transparent transition-transform duration-3 ease-out group-hover:scale-[1.03]"
          />
        )}
      </div>

      {/* ── Mitad inferior: bloque texto VIOLETA SÓLIDO ──────────────────
          `bg-primary-900` 100 % opaco + `text-cream-100` garantiza WCAG
          AAA en todo el texto, sin depender de scrim/drop-shadows. Las
          estrellas decorativas viven sólo aquí (no flotando sobre la
          foto, que ahora queda limpia). `flex-1` para que el bloque
          estire hasta el final del card cuando hay summary largo y se
          mantenga la altura uniforme de la grid via `<li className="h-full">`. */}
      <div className="relative flex flex-1 flex-col gap-3 bg-primary-900 p-6 text-cream-100 lg:p-7">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 select-none">
          <StarDot className="absolute right-5 top-4 text-[10px] opacity-50" />
          <StarDot className="absolute left-6 bottom-5 text-[10px] opacity-55" />
        </div>

        {/* Fecha + cupos: bajaron de la obra al panel. Aquí el contraste está
            garantizado por el violeta sólido y no compiten con la pieza. */}
        <div className="relative flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-pill border border-gold-500/50 px-3 py-1 text-caption font-semibold uppercase tracking-[0.08em] text-gold-500">
            <CalendarioIcon size={14} aria-hidden="true" />
            {fecha}
          </span>
          {!exposicion && (
            <span className="inline-flex items-center gap-1.5 rounded-pill border border-gold-500/40 px-3 py-1 text-caption text-gold-soft">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" strokeWidth={1.5} />
              Cupos limitados
            </span>
          )}
        </div>

        <h3 className="relative font-display text-h3 text-cream-50">{event.title}</h3>
        <p className="relative text-caption uppercase tracking-[0.12em] text-cream-100/85">
          {tipo} · {eventModalityCopy[event.modality]}
        </p>
        {exposicion ? (
          event.location_name && (
            <p className="relative text-body text-cream-100/85">{event.location_name}</p>
          )
        ) : (
          <p className="relative text-body text-cream-100/85">
            <time dateTime={event.starts_at}>{meta.time}</time>
            {event.location_name && <span> · {event.location_name}</span>}
          </p>
        )}

        {event.summary && (
          <p className="relative line-clamp-3 text-caption text-cream-100/75">{event.summary}</p>
        )}

        <div
          className={cn(
            'relative mt-auto flex items-center border-t border-cream-100/15 pt-4',
            exposicion ? 'justify-end' : 'justify-between',
          )}
        >
          {!exposicion && (
            <span className="text-body font-semibold text-gold-soft">
              {price ?? 'Cupo gratuito'}
            </span>
          )}
          <span className="text-button font-semibold uppercase tracking-[0.06em] text-gold-soft">
            Ver detalles &rarr;
          </span>
        </div>
      </div>
    </Link>
  );
}

export function EventsBlock() {
  const { data, isLoading, isError, error, refetch } = useUpcomingEvents();
  const heading = usePageCopy('home', 'seccion_eventos.heading', 'Próximos eventos');
  const [activeIdx, setActiveIdx] = useState(0);
  const items = (data ?? []).slice(0, 3);

  return (
    <section aria-labelledby="home-events-title" className="bg-cream-100 py-14 lg:py-20">
      <Container>
        <SectionHeading
          id="home-events-title"
          eyebrow={heading.toUpperCase()}
          ctaLabel="Ver todos los eventos"
          ctaHref={Routes.events}
          align="split"
        />

        {isLoading && (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} height={340} rounded="lg" />
            ))}
          </div>
        )}

        {isError && (
          <ErrorState
            title="No se pudieron cargar los eventos"
            error={error}
            onRetry={() => void refetch()}
          />
        )}

        {!isLoading && !isError && items.length === 0 && (
          <EmptyState
            illustration={<CalendarioIcon size={64} />}
            title="Aún no hay eventos publicados"
            description="Suscríbete al boletín para enterarte cuando abramos cupos."
          />
        )}

        {/* Desktop: grid 3 cols. Mobile: carrusel horizontal con snap. */}
        {!isLoading && !isError && items.length > 0 && (
          <>
            <div
              className={cn(
                'flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2',
                'lg:grid lg:grid-cols-3 lg:snap-none lg:overflow-visible lg:pb-0',
              )}
              onScroll={(e) => {
                const target = e.currentTarget;
                const idx = Math.round(target.scrollLeft / target.clientWidth);
                setActiveIdx(idx);
              }}
            >
              {items.map((ev) => (
                <div key={ev.id} className="min-w-[90%] snap-center sm:min-w-[60%] lg:min-w-0">
                  <EventDarkCard event={ev} />
                </div>
              ))}
            </div>

            {/* Indicadores tipo carrusel — sólo mobile/tablet. */}
            {items.length > 1 && (
              <div
                role="tablist"
                aria-label="Navegación de eventos"
                className="mt-5 flex items-center justify-center gap-2 lg:hidden"
              >
                {items.map((ev, idx) => (
                  <span
                    key={ev.id}
                    role="tab"
                    aria-selected={idx === activeIdx}
                    aria-label={`Evento ${idx + 1} de ${items.length}`}
                    className={cn(
                      'h-2 w-2 rounded-pill transition-colors duration-2 ease-out',
                      idx === activeIdx ? 'bg-primary-700' : 'bg-cream-200',
                    )}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </Container>
    </section>
  );
}
